(() => {
  if (window.__instantsearchAppInitialized) {
    return;
  }

  window.__instantsearchAppInitialized = true;

  const { algoliasearch, instantsearch } = window;
  const indexName = 'posters-export-all-part-01';
  const facetAttributes = [
    'designer',
    'country',
    'decade',
    'keyword',
    'medium',
  ];

  const searchClient = algoliasearch(
    'ILGVQTZOI8',
    'c7e93191ff3f7480c86ae8fb9627c9d4'
  );

  const search = instantsearch({
    indexName,
    searchClient,
    future: { preserveSharedStateOnUnmount: true },
    routing: {
      router: instantsearch.routers.history(),
      stateMapping: {
        stateToRoute(uiState) {
          const indexUiState = uiState[indexName] || {};
          const routeState = {};

          if (indexUiState.query) {
            routeState.q = indexUiState.query;
          }

          if (indexUiState.page && indexUiState.page > 1) {
            routeState.page = indexUiState.page;
          }

          facetAttributes.forEach((attribute) => {
            const refinements = indexUiState.refinementList?.[attribute];

            if (refinements && refinements.length > 0) {
              routeState[attribute] = refinements.join('||');
            }
          });

          return routeState;
        },
        routeToState(routeState) {
          const refinementList = {};

          facetAttributes.forEach((attribute) => {
            const refinements = routeState[attribute];

            if (typeof refinements === 'string' && refinements.length > 0) {
              refinementList[attribute] = refinements
                .split('||')
                .filter(Boolean);
            }
          });

          const page = Number(routeState.page);

          return {
            [indexName]: {
              query: typeof routeState.q === 'string' ? routeState.q : '',
              refinementList,
              page: Number.isFinite(page) && page > 0 ? page : 1,
            },
          };
        },
      },
    },
  });

  const createFacetWidget = ({ container, attribute, title }) =>
    instantsearch.widgets.panel({
      templates: {
        header: title,
      },
    })(instantsearch.widgets.refinementList)({
      container,
      attribute,
      searchable: true,
      showMore: true,
      limit: 10,
      showMoreLimit: 30,
    });

  const renderActiveFilters = ({ items, refine, widgetParams }) => {
    const container = widgetParams.container;

    if (!container) {
      return;
    }

    const selectedItems = items.filter((item) =>
      facetAttributes.includes(item.attribute)
    );
    const tags = selectedItems.flatMap((item) =>
      item.refinements.map((refinement) => ({ item, refinement }))
    );

    container.innerHTML = '';

    if (tags.length === 0) {
      return;
    }

    const tagsWrapper = document.createElement('div');
    tagsWrapper.className = 'active-filters';

    tags.forEach(({ item, refinement }) => {
      const tagButton = document.createElement('button');
      tagButton.type = 'button';
      tagButton.className = 'active-filters__tag';
      tagButton.setAttribute(
        'aria-label',
        `Remove ${item.label}: ${refinement.label}`
      );
      tagButton.textContent = `${item.label}: ${refinement.label} ×`;
      tagButton.addEventListener('click', () => refine(refinement));
      tagsWrapper.appendChild(tagButton);
    });

    container.appendChild(tagsWrapper);
  };

  const activeFiltersWidget =
    instantsearch.connectors.connectCurrentRefinements(renderActiveFilters);

  const decodeHtmlEntities = (value) => {
    let decoded = String(value || '');

    for (let i = 0; i < 2; i += 1) {
      const parser = document.createElement('textarea');
      parser.innerHTML = decoded;
      const nextValue = parser.value;

      if (nextValue === decoded) {
        break;
      }

      decoded = nextValue;
    }

    return decoded;
  };

  const sanitizeDescription = (value) =>
    decodeHtmlEntities(value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const renderDescriptionWithHighlights = (hit, html) => {
    const description = sanitizeDescription(hit.descriptions);
    const matchedWords = hit._highlightResult?.descriptions?.matchedWords || [];

    if (matchedWords.length === 0) {
      return description;
    }

    const alternatives = [...new Set(matchedWords.filter(Boolean))]
      .sort((a, b) => b.length - a.length)
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (alternatives.length === 0) {
      return description;
    }

    const regex = new RegExp(`(${alternatives.join('|')})`, 'gi');
    const parts = description.split(regex);

    return html`${parts.map((part, index) =>
      index % 2 === 1
        ? html`<mark class="ais-Highlight-highlighted">${part}</mark>`
        : part
    )}`;
  };

  search.addWidgets([
    instantsearch.widgets.searchBox({
      container: '#searchbox',
    }),
    instantsearch.widgets.stats({
      container: '#stats',
      templates: {
        text: ({ nbHits }) => `${nbHits.toLocaleString()} results`,
      },
    }),
    activeFiltersWidget({
      container: document.querySelector('#active-filters'),
    }),
    instantsearch.widgets.clearRefinements({
      container: '#clear-filters',
      includedAttributes: facetAttributes,
      templates: {
        resetLabel: 'Clear Filters',
      },
    }),
    createFacetWidget({
      container: '#designer-list',
      attribute: 'designer',
      title: 'Designer',
    }),
    createFacetWidget({
      container: '#country-list',
      attribute: 'country',
      title: 'Country',
    }),
    createFacetWidget({
      container: '#decade-list',
      attribute: 'decade',
      title: 'Decade',
    }),
    createFacetWidget({
      container: '#keyword-list',
      attribute: 'keyword',
      title: 'Keyword',
    }),
    createFacetWidget({
      container: '#medium-list',
      attribute: 'medium',
      title: 'Medium',
    }),
    instantsearch.widgets.hits({
      container: '#hits',
      templates: {
        item: (hit, { html, components }) => html`
          <article>
            ${hit.permalink
              ? html`<a
                  href=${hit.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  ><img src=${hit.image} alt=${hit.title}
                /></a>`
              : html`<img src=${hit.image} alt=${hit.title} />`}
            <div>
              <h1>
                ${hit.permalink
                  ? html`<a
                      href=${hit.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      >${components.Highlight({ hit, attribute: 'title' })}</a
                    >`
                  : components.Highlight({ hit, attribute: 'title' })}
              </h1>
              <p>${components.Highlight({ hit, attribute: 'year' })}</p>
              <p>${components.Highlight({ hit, attribute: 'medium' })}</p>
              <p>${renderDescriptionWithHighlights(hit, html)}</p>
            </div>
          </article>
        `,
      },
    }),
    instantsearch.widgets.configure({
      hitsPerPage: 20,
    }),
    instantsearch.widgets.pagination({
      container: '#pagination',
    }),
  ]);

  search.start();
})();
